import React from 'react';
import styled from 'styled-components';
import {Button, Checkbox, Detail, EMPHASIS, SubHeading, Text} from '@forecast-it/design-system';
import {FeatureFlagAssignmentRow} from './featureFlagDetailData';
import {areAllCompaniesSelected} from './featureFlagRemoval';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_CARD_HOVER_BACKGROUND,
	FEATURE_FLAG_NEUTRAL_FILL,
	featureFlagCardSurface,
} from './featureFlagSurface';

// Shared by the column header and the rows so the two can never drift apart, the same way the flag
// list shares its grid. Narrower than the list's, because this table has four columns rather than six.
const ASSIGNMENT_COLUMNS = 'minmax(180px, 1.1fr) minmax(240px, 1.5fr) 130px 44px';
const ASSIGNMENT_GAP = '16px';
const ASSIGNMENT_MIN_WIDTH = '860px';

const TableCard = styled.div`
	${featureFlagCardSurface}
	margin-top: 20px;
	overflow-x: auto;
`;

const TableHead = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 20px;
	padding: 14px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const TableTitle = styled.div`
	display: flex;
	align-items: baseline;
	gap: 10px;
`;

// Holds the column header and the rows, and nothing else, so the whole of it — and only it — can
// announce as a table. It is what carries the width the columns need, with the header and rows
// stretching to fill it, so the card around it scrolls them together.
const TableGrid = styled.div`
	min-width: ${ASSIGNMENT_MIN_WIDTH};
`;

const ColumnHeader = styled.div`
	display: grid;
	grid-template-columns: ${ASSIGNMENT_COLUMNS};
	gap: ${ASSIGNMENT_GAP};
	padding: 9px 18px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

/**
 * A column heading, set small and wide-tracked so it reads as a label rather than as a line of copy.
 *
 * The tracking has to be declared on the child rather than here: the typography components emit their
 * own letter-spacing onto the element they render, and a declaration on that element beats one
 * inherited from this wrapper.
 *
 * Laid out as a row rather than left to inline flow, because in selection mode the COMPANY heading
 * gains the select-all box as a second child. This element is a grid item, so it is blockified, and
 * any block-level child inside it would take a line of its own and push the label underneath. A row
 * also centres the box against the label the way the row boxes below are centred, which inline flow
 * would only approximate.
 */
const MicroLabel = styled.span`
	display: flex;
	align-items: center;
	gap: 12px;

	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

/**
 * Holds the select-all box beside the COMPANY label.
 *
 * Keeps its own size, so a narrow header shrinks the label rather than squeezing the box: it is a flex
 * item of the heading, and would otherwise be shrinkable like any other.
 */
const SelectAllCell = styled.span`
	display: flex;
	align-items: center;
	flex: none;
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: ${ASSIGNMENT_COLUMNS};
	gap: ${ASSIGNMENT_GAP};
	align-items: center;
	min-height: 44px;
	padding: 8px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	&:hover {
		background: ${FEATURE_FLAG_CARD_HOVER_BACKGROUND};
	}
`;

// The grid rows announce as table rows, so the label and date each need a cell of their own to be
// announced in — the typography component alone is the content, not the cell.
const Cell = styled.span`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;

const UsersCell = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const ActionCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 56px 20px;
`;

/**
 * Everything the table needs to let companies be picked, supplied as one value.
 *
 * Its presence is what puts the table into selection mode — there is no separate flag to fall out of
 * step with it, and no way to ask for checkboxes without also saying what happens when one is ticked.
 */
export interface FeatureFlagAssignmentSelection {
	/** Encoded company ids currently ticked. */
	selectedCompanyIds: readonly string[];
	/** Tick or untick one company. */
	onToggleCompany: (companyId: string) => void;
	/** Tick every company, or clear them all if they are already ticked. */
	onToggleAll: () => void;
}

export interface FeatureFlagAssignmentTableProps {
	rows: readonly FeatureFlagAssignmentRow[];
	/** Called with the row's encoded company id, to edit that company's access. */
	onEditCompany: (companyId: string) => void;
	/**
	 * Control rendered at the right of the table's own header, beside the count. The page owns it,
	 * because what it offers depends on state the table does not hold.
	 */
	headerAction?: React.ReactNode;
	/** Omitted unless companies are being picked for removal. */
	selection?: FeatureFlagAssignmentSelection;
}

export function FeatureFlagAssignmentTable({
	rows,
	onEditCompany,
	headerAction,
	selection,
}: FeatureFlagAssignmentTableProps): JSX.Element {
	// Counts the companies listed below, so it includes companies that hold the flag only through
	// named users. That makes it legitimately larger than the Companies stat card, which counts
	// all-users grants.
	const countLabel =
		rows.length === 0 ? 'No companies assigned' : rows.length === 1 ? '1 company' : `${rows.length} companies`;

	const selectedCompanyIds = selection?.selectedCompanyIds ?? [];
	const allSelected = areAllCompaniesSelected(
		selectedCompanyIds,
		rows.map(row => row.companyId)
	);

	return (
		<TableCard>
			<TableHead>
				<TableTitle>
					<SubHeading>Currently assigned</SubHeading>
					<Detail color="medium">{countLabel}</Detail>
				</TableTitle>
				{headerAction}
			</TableHead>
			{/* A CSS grid rather than a real table, because the layout is card-and-grid throughout this
			    feature — so the roles have to say out loud what the markup would otherwise have implied. */}
			<TableGrid role="table" aria-label="Companies with this flag">
				<ColumnHeader role="row">
					<MicroLabel role="columnheader">
						{selection ? (
							<SelectAllCell>
								{/* Named rather than left to the design system's generic "Select row": this box
								    selects the whole table, and a screen reader announcing it as a row would
								    describe the wrong scope. */}
								<Checkbox
									aria-label="Select all companies"
									checked={allSelected}
									onChange={selection.onToggleAll}
								/>
							</SelectAllCell>
						) : null}
						<Detail size="2">COMPANY</Detail>
					</MicroLabel>
					<MicroLabel role="columnheader">
						<Detail size="2">USERS</Detail>
					</MicroLabel>
					<MicroLabel role="columnheader">
						<Detail size="2">ADDED</Detail>
					</MicroLabel>
					{/* The edit column is headed by nothing on the page, so its name is given here rather
					    than leaving a screen reader to announce a nameless fourth column. */}
					<span role="columnheader" aria-label="Actions" />
				</ColumnHeader>
				{rows.map(row => (
					<Row key={row.companyId} data-cy="feature-flag-assignment-row" role="row">
						<Cell role="cell">
							{selection ? (
								// Named after the company, not "Select row": with a dozen rows on screen, boxes
								// that all announce the same thing give no way to tell which one has focus.
								<Checkbox
									aria-label={`Select ${row.companyName}`}
									checked={selectedCompanyIds.includes(row.companyId)}
									onChange={() => selection.onToggleCompany(row.companyId)}
								/>
							) : null}
							<Text>{row.companyName}</Text>
						</Cell>
						<UsersCell role="cell">
							{row.allUsers ? (
								<Detail>All users</Detail>
							) : (
								<>
									<Text>{row.userCountLabel}</Text>
									{row.users.map((user, index) => (
										<Detail color="medium" key={`${row.companyId}-${index}`}>
											{user}
										</Detail>
									))}
								</>
							)}
						</UsersCell>
						<Cell role="cell">
							<Detail color="medium">{row.added ?? '—'}</Detail>
						</Cell>
						<ActionCell role="cell">
							<Button
								aria-label={`Edit access for ${row.companyName}`}
								emphasis={EMPHASIS.LOW}
								icon="edit"
								iconOnly
								onPress={() => onEditCompany(row.companyId)}
							/>
						</ActionCell>
					</Row>
				))}
			</TableGrid>
			{rows.length === 0 ? (
				<EmptyState>
					<SubHeading>No companies have this flag yet</SubHeading>
					<Detail>{'Use “Roll out to more companies” to start a percentage rollout or add one company.'}</Detail>
				</EmptyState>
			) : null}
		</TableCard>
	);
}
